import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingModal = ({ open, onClose, presetCategory }) => {
  const levelForCategory = (cat) => {
    if (cat === 'College') return 'College';
    if (cat === 'High School') return 'High School';
    if (cat === 'WNBA') return 'Pro';
    return 'Pro';
  };

  const [formData, setFormData] = useState({
    athlete_name: '', email: '', sport: presetCategory || '',
    level: levelForCategory(presetCategory), instagram: '', story: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setFormData({
        athlete_name: '', email: '',
        sport: presetCategory && presetCategory !== 'College' && presetCategory !== 'High School' ? presetCategory : '',
        level: levelForCategory(presetCategory),
        instagram: '', story: ''
      });
      setSubmitted(false);
      setError(null);
    }
  }, [open, presetCategory]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/applications`, formData);
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-4"
      onClick={onClose}
      data-testid="booking-modal-overlay"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl max-w-xl w-full max-h-[100dvh] sm:max-h-[calc(100dvh-4rem)] relative sm:my-8 overscroll-contain flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="booking-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full bg-white/95 hover:bg-gray-100 transition z-20 shadow-md"
          aria-label="Close"
          data-testid="booking-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div
          className="overflow-y-auto flex-1 rounded-t-2xl sm:rounded-2xl"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
        <div className="bg-black text-white p-5 sm:p-8 pr-14">
          <p className="font-italic text-xs sm:text-sm text-blue-300 mb-2">Be part of the story</p>
          <h2 className="font-display text-2xl sm:text-3xl uppercase leading-tight">
            We&apos;re <span className="blue-gradient">Booking</span>
            {presetCategory && presetCategory !== 'All' ? ` ${presetCategory}` : ''} Athletes
          </h2>
          <p className="font-body text-sm text-gray-300 mt-3">
            Reserve your slot. Tell us who you are and we&apos;ll be in touch.
          </p>
        </div>

        <div className="p-5 sm:p-8">
          {submitted ? (
            <div className="text-center py-8" data-testid="booking-success">
              <h3 className="font-display text-3xl uppercase mb-3">You&apos;re In Line</h3>
              <p className="font-body text-gray-600 mb-6">
                Thanks {formData.athlete_name || 'champ'} — we received your application and will reach out shortly.
              </p>
              <button
                onClick={onClose}
                className="btn-pill bg-black text-white"
                data-testid="booking-success-close"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
              <div>
                <label className="block font-body font-bold text-sm mb-1">Full Name</label>
                <input
                  type="text" required
                  value={formData.athlete_name}
                  onChange={(e) => setFormData({ ...formData, athlete_name: e.target.value })}
                  data-testid="booking-name-input"
                />
              </div>
              <div>
                <label className="block font-body font-bold text-sm mb-1">Email</label>
                <input
                  type="email" required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="booking-email-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-body font-bold text-sm mb-1">Sport</label>
                  <input
                    type="text" required
                    value={formData.sport}
                    onChange={(e) => setFormData({ ...formData, sport: e.target.value })}
                    placeholder="e.g. Basketball"
                    data-testid="booking-sport-input"
                  />
                </div>
                <div>
                  <label className="block font-body font-bold text-sm mb-1">Level</label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    data-testid="booking-level-input"
                  >
                    <option value="Pro">Pro</option>
                    <option value="College">College</option>
                    <option value="Draft Prospect">Draft Prospect</option>
                    <option value="High School">High School</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block font-body font-bold text-sm mb-1">Instagram <span className="font-normal text-gray-500">(optional)</span></label>
                <input
                  type="text"
                  value={formData.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                  placeholder="@yourhandle"
                  data-testid="booking-instagram-input"
                />
              </div>
              <div>
                <label className="block font-body font-bold text-sm mb-1">Your Story</label>
                <textarea
                  required rows={4}
                  value={formData.story}
                  onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                  placeholder="What makes your journey worth telling?"
                  data-testid="booking-story-input"
                />
              </div>

              {error && <p className="text-red-600 text-sm font-body" data-testid="booking-error">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="btn-pill bg-black text-white w-full disabled:opacity-50"
                data-testid="booking-submit-btn"
              >
                {submitting ? 'Submitting…' : 'Reserve My Slot'}
              </button>
            </form>
          )}
        </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BookingModal;
